import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { trpc } from "@/lib/trpc";
import ClarityLogo from "@/assets/clarity-logo.svg";
import {
  resolveCanvasFinishImagePath,
  CANVAS_NEUTRAL_PREVIEW_IMAGE,
} from "@shared/canvasCatalogue/finishImageAuthority";
import { type CanvasGrilleCode } from "@shared/canvasCatalogue/types";
import {
  CANVAS_OWN_WALL_MOUNT_NAME,
  CANVAS_OWN_WALL_MOUNT_SKU,
} from "@shared/canvasCatalogue/publicEvent";
import { Check, Mail, Smartphone, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";
import { toast } from "sonner";

const money = new Intl.NumberFormat("en-SG", {
  style: "currency",
  currency: "SGD",
  maximumFractionDigits: 0,
});
type Config = {
  productFormat: "tv" | "solo";
  tvSizeInches: number | null;
  grilleCode: CanvasGrilleCode;
  finish: string;
  accessorySku: string | null;
};

const PUBLIC_GRILLE_NAMES: Record<CanvasGrilleCode, string> = {
  S: "Standard Fabric",
  K: "Kvadrat Fabric",
  W: "Wood Grille",
};

type CanvasLocale = "en" | "zh-TW";

const TW_GRILLE_NAMES: Record<CanvasGrilleCode, string> = {
  S: "標準布料",
  K: "Kvadrat 布料",
  W: "木質格柵",
};

const TW_FINISH_NAMES: Record<string, string> = {
  Black: "黑色",
  "Light Grey": "淺灰色",
  "Light-Grey": "淺灰色",
  "Structure Grey": "結構灰",
  "Dark Blue": "深藍色",
  "Ocean Grey": "海洋灰",
  "Light Green": "淺綠色",
  Umber: "赭褐色",
  "Burnt Umber": "深赭褐色",
  "Organic Green": "有機綠",
  "Milled Ochre": "赭黃色",
  "Light Oak": "淺橡木",
  Mahogany: "桃花心木",
  Walnut: "胡桃木",
};

const COPY = {
  en: {
    opening: "Opening your proposal…",
    unavailable: "This proposal link is invalid or unavailable.",
    proposal: "Canvas proposal",
    configuration: "Your Canvas configuration",
    selectedFinish: "Selected Canvas finish",
    solo: "Solo",
    total: "Total",
    confirmed:
      "Confirmed. The Clarity team will contact you to arrange the next step.",
    edit: "Edit configuration",
    confirming: "Confirming…",
    confirm: "Confirm configuration",
    eyebrow: "Designed around your television",
    hero: "Make the screen sound as beautiful as it looks.",
    intro:
      "Choose your size, finish and mounting style. Your price updates instantly.",
    pricingIncludes:
      "Prices include tax, delivery, installation and audio calibration.",
    format: "1. Format",
    canvasTv: "Canvas for TV",
    tvSize: "TV size",
    grille: "2. Grille",
    finish: "3. Finish",
    mount: "4. Wall mount",
    includedMount: "Included standing floor mount",
    ownMount: "I have my own mount",
    selectStand: "Select stand",
    enlarge: "Enlarge",
    enlargeImage: "Enlarge image of",
    selectConfiguration: "Select your configuration",
    sendProposal: "Email or send this proposal to me",
    yourName: "Your name",
    email: "Email",
    emailAddress: "Email address",
    mobileNumber: "Mobile number",
    sendCode: "Send verification code",
    sixDigitCode: "6-digit code",
    verify: "Verify",
    marketing:
      "I would like to receive occasional news and event invitations from Clarity. Optional.",
    creating: "Creating proposal…",
    sendMine: "Send my proposal",
    closeImage: "Close enlarged image",
    codeSent: "Code sent to",
    verified: "Verified",
  },
  "zh-TW": {
    opening: "正在開啟您的提案…",
    unavailable: "此提案連結無效或目前無法使用。",
    proposal: "Canvas 提案",
    configuration: "您的 Canvas 配置",
    selectedFinish: "所選 Canvas 飾面",
    solo: "獨立音響",
    total: "總計",
    confirmed: "已確認。愷銳音響團隊將與您聯絡並安排後續事宜。",
    edit: "修改配置",
    confirming: "確認中…",
    confirm: "確認配置",
    eyebrow: "為您的電視量身打造",
    hero: "讓螢幕的聲音，與畫面一樣動人。",
    intro: "選擇尺寸、飾面與安裝方式，價格將即時更新。",
    pricingIncludes: "價格均包含稅金、配送、安裝及音響校正。",
    format: "1. 形式",
    canvasTv: "Canvas 電視款",
    tvSize: "電視尺寸",
    grille: "2. 格柵",
    finish: "3. 飾面",
    mount: "4. 安裝方式",
    includedMount: "隨附落地支架",
    ownMount: "我有自己的支架",
    selectStand: "選擇支架",
    enlarge: "放大",
    enlargeImage: "放大圖片：",
    selectConfiguration: "請選擇您的配置",
    sendProposal: "取得愷銳音響專屬提案",
    yourName: "您的姓名",
    email: "電子郵件",
    emailAddress: "電子郵件地址",
    mobileNumber: "手機號碼",
    sendCode: "傳送驗證碼",
    sixDigitCode: "6 位數驗證碼",
    verify: "驗證",
    marketing: "我願意接收愷銳音響的最新消息與活動邀請（選填）。",
    creating: "正在建立提案…",
    sendMine: "傳送我的提案",
    closeImage: "關閉放大圖片",
    codeSent: "驗證碼已傳送至",
    verified: "驗證成功",
  },
} as const;

function localFinish(name: string, locale: CanvasLocale) {
  return locale === "zh-TW" ? (TW_FINISH_NAMES[name] ?? name) : name;
}

function localCanvasModel(
  fallback: string,
  configuration: Pick<Config, "productFormat" | "tvSizeInches" | "grilleCode">,
  locale: CanvasLocale
) {
  if (locale !== "zh-TW") return fallback;
  const format =
    configuration.productFormat === "solo"
      ? "Canvas Solo"
      : `Canvas ${configuration.tvSizeInches} 吋`;
  return `${format} — ${TW_GRILLE_NAMES[configuration.grilleCode]}`;
}

function localAccessory(accessory: any, locale: CanvasLocale) {
  if (locale !== "zh-TW") return accessory.name;
  if (accessory.sku === CANVAS_OWN_WALL_MOUNT_SKU)
    return COPY["zh-TW"].ownMount;
  if (accessory.sku === "PS80") return "Future Automation PS80 可動式壁掛架";
  const sizeFromName = accessory.name?.match(/(55–65|75–85)/)?.[1];
  const size = accessory.sizes?.length
    ? `${Math.min(...accessory.sizes)}–${Math.max(...accessory.sizes)}`
    : sizeFromName;
  const silver =
    accessory.finish === "Silver" || accessory.name?.includes("Silver");
  return `Canvas 旋轉支架${size ? ` ${size} 吋` : ""} — ${silver ? "銀色" : "黑色"}`;
}

function localAccessoryDescription(accessory: any, locale: CanvasLocale) {
  if (locale !== "zh-TW" || accessory.sku !== "PS80")
    return accessory.description;
  return "適用於 80–110 吋電視的重型可動式壁掛架。單手即可平順拉出電視，方便整理線材或新增連接。最大承重 120 公斤，建議搭配 83 吋以上的 Canvas 使用。";
}

function Shell({
  children,
  locale,
}: {
  children: React.ReactNode;
  locale: CanvasLocale;
}) {
  const isTaiwan = locale === "zh-TW";
  return (
    <main
      lang={locale}
      data-canvas-market={isTaiwan ? "keep-running" : "clarity"}
      className={
        isTaiwan
          ? "min-h-screen bg-[#F1EFEA] text-[#171717]"
          : "min-h-screen bg-[#F4F1EA] text-[#17233D]"
      }
      style={{
        fontFamily: isTaiwan
          ? '"PingFang TC", "Noto Sans TC", "Microsoft JhengHei", sans-serif'
          : '"Avenir Next", Avenir, "Helvetica Neue", sans-serif',
      }}
    >
      {isTaiwan ? (
        <header className="sticky top-0 z-50 border-b border-black/10 bg-white/95 px-5 py-3 shadow-[0_4px_24px_rgba(0,0,0,0.04)] backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-5">
            <img
              src="/catalogue-configurator/canvas/keep-running-logo.png"
              alt="愷銳音響 KEEP RUNNING"
              className="h-11 w-auto sm:h-12"
            />
            <div className="border-l border-black/15 pl-4 text-right">
              <span className="block text-[11px] font-semibold uppercase tracking-[0.22em] text-[#171717]">
                Canvas HiFi
              </span>
              <span className="mt-0.5 block text-[9px] uppercase tracking-[0.18em] text-[#D71920]">
                Taiwan Edition
              </span>
            </div>
          </div>
        </header>
      ) : (
        <header className="sticky top-0 z-50 border-b border-white/10 bg-[#17233D] px-5 py-4 text-white shadow-sm">
          <div className="mx-auto flex max-w-6xl items-center justify-between">
            <span className="text-xs font-medium uppercase tracking-[0.18em] text-[#D6B36A]">
              Canvas HiFi
            </span>
            <img src={ClarityLogo} alt="Clarity" className="h-7 w-auto" />
          </div>
        </header>
      )}
      {children}
      {isTaiwan ? (
        <footer className="border-t border-black/10 bg-[#171717] px-5 py-8 text-center text-[10px] uppercase tracking-[0.24em] text-white/60">
          KEEP RUNNING · 愷銳音響 · SINCE 1988
        </footer>
      ) : (
        <footer className="px-5 py-10 text-center text-xs text-[#17233D]/50">
          THE CLARITY GROUP
        </footer>
      )}
    </main>
  );
}

function QuoteView({ token, locale }: { token: string; locale: CanvasLocale }) {
  const [, navigate] = useLocation();
  const t = COPY[locale];
  const isTaiwan = locale === "zh-TW";
  const quote = trpc.canvasPublic.getQuote.useQuery({ token });
  const confirm = trpc.canvasPublic.confirm.useMutation({
    onSuccess: () => void quote.refetch(),
  });
  if (quote.isLoading)
    return (
      <Shell locale={locale}>
        <div className="mx-auto max-w-3xl p-8">{t.opening}</div>
      </Shell>
    );
  if (!quote.data)
    return (
      <Shell locale={locale}>
        <div className="mx-auto max-w-3xl p-8">{t.unavailable}</div>
      </Shell>
    );
  const c = quote.data.configuration as any;
  return (
    <Shell locale={locale}>
      <div className="mx-auto max-w-3xl px-5 py-10">
        <p
          className={`text-xs font-semibold uppercase tracking-[.18em] ${isTaiwan ? "text-[#D71920]" : "text-[#9A7A3A]"}`}
        >
          {t.proposal} · {quote.data.reference}
        </p>
        <h1
          className="mt-3 text-4xl font-light"
          style={
            isTaiwan
              ? { fontFamily: '"Songti TC", "Noto Serif TC", serif' }
              : undefined
          }
        >
          {t.configuration}
        </h1>
        <div
          className={`mt-8 overflow-hidden border bg-white ${isTaiwan ? "rounded-sm border-black/10 shadow-[0_18px_55px_rgba(0,0,0,0.08)]" : "rounded-2xl border-[#17233D]/10"}`}
        >
          <img
            className="h-72 w-full object-cover"
            src={
              resolveCanvasFinishImagePath(c.finish, c.productFormat) ??
              CANVAS_NEUTRAL_PREVIEW_IMAGE
            }
            alt={t.selectedFinish}
          />
          <div className="space-y-3 p-6">
            <div className="flex justify-between gap-4">
              <span>{localCanvasModel(c.canvas?.name, c, locale)}</span>
              <strong>{money.format(c.canvas?.priceSgd ?? 0)}</strong>
            </div>
            <p className="text-sm text-[#17233D]/65">
              {c.tvSizeInches
                ? `${c.tvSizeInches} ${locale === "zh-TW" ? "吋" : "inch"} · `
                : `${t.solo} · `}
              {
                (locale === "zh-TW" ? TW_GRILLE_NAMES : PUBLIC_GRILLE_NAMES)[
                  c.grilleCode as CanvasGrilleCode
                ]
              }{" "}
              · {localFinish(c.finish, locale)}
            </p>
            {c.accessory ? (
              <div className="flex justify-between gap-4 border-t pt-3">
                <span>{localAccessory(c.accessory, locale)}</span>
                <strong>{money.format(c.accessory.priceSgd)}</strong>
              </div>
            ) : null}
            <div className="flex justify-between border-t pt-4 text-xl">
              <span>{t.total}</span>
              <strong>{money.format(quote.data.totalAmount)}</strong>
            </div>
          </div>
        </div>
        {quote.data.status === "confirmed" ? (
          <div className="mt-6 rounded-xl bg-emerald-50 p-5 text-emerald-900">
            <Check className="mr-2 inline h-5 w-5" />
            {t.confirmed}
          </div>
        ) : (
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <Button
              variant="outline"
              className="h-12"
              onClick={() =>
                navigate(
                  `${locale === "zh-TW" ? "/canvas/krtw" : "/canvas"}?edit=${encodeURIComponent(token)}`
                )
              }
            >
              {t.edit}
            </Button>
            <Button
              className={`h-12 ${isTaiwan ? "bg-[#D71920] hover:bg-[#B51218]" : "bg-[#17233D]"}`}
              disabled={confirm.isPending}
              onClick={() => confirm.mutate({ token })}
            >
              {confirm.isPending ? t.confirming : t.confirm}
            </Button>
          </div>
        )}
      </div>
    </Shell>
  );
}

export default function CanvasPublicPage() {
  const pathname = window.location.pathname.replace(/\/$/, "");
  const locale: CanvasLocale = pathname.startsWith("/canvas/krtw")
    ? "zh-TW"
    : "en";
  const [twMatched, twParams] = useRoute("/canvas/krtw/:token");
  const [matched, params] = useRoute("/canvas/:token");
  const token =
    pathname === "/canvas/krtw"
      ? null
      : twMatched
        ? twParams?.token
        : matched
          ? params?.token
          : null;
  if (token) return <QuoteView token={token} locale={locale} />;
  return <Configurator locale={locale} />;
}

function Configurator({ locale }: { locale: CanvasLocale }) {
  const [, navigate] = useLocation();
  const t = COPY[locale];
  const isTaiwan = locale === "zh-TW";
  const meta = trpc.canvasPublic.meta.useQuery();
  const requestOtp = trpc.canvasPublic.requestOtp.useMutation();
  const verifyOtp = trpc.canvasPublic.verifyOtp.useMutation();
  const submit = trpc.canvasPublic.submit.useMutation();
  const track = trpc.canvasPublic.track.useMutation();
  const editToken = new URLSearchParams(window.location.search).get("edit");
  const prior = trpc.canvasPublic.getQuote.useQuery(
    { token: editToken || "not-enabled-token-placeholder" },
    { enabled: Boolean(editToken) }
  );
  const sessionId = useRef(crypto.randomUUID()).current;
  const [config, setConfig] = useState<Config>({
    productFormat: "tv",
    tvSizeInches: 65,
    grilleCode: "S",
    finish: "Black",
    accessorySku: null,
  });
  const [name, setName] = useState("");
  const [channel, setChannel] = useState<"email" | "phone">("email");
  const [destination, setDestination] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [verificationToken, setVerificationToken] = useState<string | null>(
    null
  );
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");
  const [expandedAccessory, setExpandedAccessory] = useState<{
    name: string;
    imagePath: string;
  } | null>(null);
  useEffect(() => {
    track.mutate({
      sessionId,
      campaign:
        locale === "zh-TW" ? "canvas-hifi-event-tw" : "canvas-hifi-event",
      eventType: editToken ? "edit_clicked" : "landing_view",
      occurrence: "page",
    });
  }, []);
  useEffect(() => {
    const c = prior.data?.configuration as any;
    if (c)
      setConfig({
        productFormat: c.productFormat,
        tvSizeInches: c.tvSizeInches,
        grilleCode: c.grilleCode,
        finish: c.finish,
        accessorySku: c.accessorySku ?? c.accessory?.sku ?? null,
      });
  }, [prior.data]);
  useEffect(() => {
    if (!expandedAccessory) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setExpandedAccessory(null);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [expandedAccessory]);
  const variant = useMemo(
    () =>
      meta.data?.variants.find(
        v =>
          v.productFormat === config.productFormat &&
          v.grilleCode === config.grilleCode &&
          (config.productFormat === "solo"
            ? v.tvSizeInches == null
            : v.tvSizeInches === config.tvSizeInches)
      ),
    [config, meta.data]
  );
  useEffect(() => {
    if (variant && !variant.finishChoices.includes(config.finish))
      setConfig(c => ({ ...c, finish: variant.finishChoices[0] ?? "Black" }));
  }, [variant]);
  const accessories = useMemo(
    () =>
      config.productFormat === "solo"
        ? []
        : [
            ...(meta.data?.stands ?? []).filter(s =>
              s.sizes.map(Number).includes(config.tvSizeInches ?? 0)
            ),
            ...(meta.data?.ps80 && [83, 85].includes(config.tvSizeInches ?? 0)
              ? [meta.data.ps80]
              : []),
          ],
    [config, meta.data]
  );
  const accessory = accessories.find(a => a.sku === config.accessorySku);
  const total = Number(variant?.sgd ?? 0) + Number(accessory?.sgd ?? 0);
  const sendCode = async () => {
    try {
      const r = await requestOtp.mutateAsync({ channel, destination });
      setChallengeId(r.challengeId);
      toast.success(`${t.codeSent} ${r.maskedDestination}`);
    } catch (e: any) {
      toast.error(e.message);
    }
  };
  const verify = async () => {
    if (!challengeId) return;
    try {
      const r = await verifyOtp.mutateAsync({ challengeId, code });
      setVerificationToken(r.verificationToken);
      toast.success(t.verified);
    } catch (e: any) {
      toast.error(e.message);
    }
  };
  const finish = async () => {
    if (!verificationToken || !variant) return;
    try {
      const r = await submit.mutateAsync({
        verificationToken,
        sourceToken: editToken ?? undefined,
        name,
        email: channel === "email" ? destination : undefined,
        phone: channel === "phone" ? destination : undefined,
        campaign:
          locale === "zh-TW" ? "canvas-hifi-event-tw" : "canvas-hifi-event",
        sessionId,
        configuration: config,
        marketingConsent: consent,
        website,
      });
      const proposalPath = new URL(r.url, window.location.origin).pathname;
      const proposalToken = proposalPath.split("/").pop();
      navigate(
        locale === "zh-TW" && proposalToken
          ? `/canvas/krtw/${proposalToken}`
          : proposalPath
      );
    } catch (e: any) {
      toast.error(e.message);
    }
  };
  const chip = (active: boolean) =>
    `rounded-full border px-4 py-2 text-sm transition ${active ? (isTaiwan ? "border-[#D71920] bg-[#171717] text-white shadow-[inset_3px_0_0_#D71920]" : "border-[#D6B36A] bg-[#17233D] text-white") : isTaiwan ? "border-black/15 bg-white hover:border-[#D71920]/60" : "border-[#17233D]/15 bg-white"}`;
  const selectedOption = isTaiwan
    ? "border-[#D71920] bg-[#171717] text-white shadow-[inset_3px_0_0_#D71920]"
    : "border-[#D6B36A] bg-[#17233D] text-white";
  const plainOption = isTaiwan ? "border-black/10" : "border-[#17233D]/10";
  return (
    <Shell locale={locale}>
      <div
        className={`mx-auto max-w-6xl px-5 ${isTaiwan ? "py-12 sm:py-16" : "py-9"}`}
      >
        <div className="mb-8 max-w-3xl">
          <div className={isTaiwan ? "border-l-2 border-[#D71920] pl-4" : ""}>
            <p
              className={`text-xs font-semibold uppercase tracking-[.2em] ${isTaiwan ? "text-[#D71920]" : "text-[#9A7A3A]"}`}
            >
              {t.eyebrow}
            </p>
          </div>
          <h1
            className={`mt-3 font-normal leading-[1.06] tracking-[-0.025em] ${isTaiwan ? "max-w-2xl text-4xl sm:text-[4.2rem]" : "text-4xl sm:text-6xl"}`}
            style={{
              fontFamily: isTaiwan
                ? '"Songti TC", "Noto Serif TC", "PMingLiU", serif'
                : '"Iowan Old Style", Baskerville, Georgia, serif',
            }}
          >
            {t.hero}
          </h1>
          <p
            className={`mt-4 ${isTaiwan ? "text-black/55" : "text-[#17233D]/65"}`}
          >
            {t.intro}
          </p>
          <p
            className={`mt-2 text-sm font-medium ${isTaiwan ? "text-black/70" : "text-[#17233D]/75"}`}
          >
            {t.pricingIncludes}
          </p>
        </div>
        <div className="grid gap-7 lg:grid-cols-[1fr_400px]">
          <div
            className={`space-y-7 bg-white p-5 sm:p-7 ${isTaiwan ? "rounded-sm border border-black/10 shadow-[0_18px_55px_rgba(0,0,0,0.07)]" : "rounded-2xl shadow-sm"}`}
          >
            <section>
              <h2 className="mb-3 text-sm font-semibold">{t.format}</h2>
              <div className="flex flex-wrap gap-2">
                <button
                  className={chip(config.productFormat === "tv")}
                  onClick={() =>
                    setConfig(c => ({
                      ...c,
                      productFormat: "tv",
                      tvSizeInches: c.tvSizeInches || 65,
                    }))
                  }
                >
                  {t.canvasTv}
                </button>
                <button
                  className={chip(config.productFormat === "solo")}
                  onClick={() =>
                    setConfig(c => ({
                      ...c,
                      productFormat: "solo",
                      tvSizeInches: null,
                      accessorySku: null,
                    }))
                  }
                >
                  Canvas Solo
                </button>
              </div>
              {config.productFormat === "tv" ? (
                <div className="mt-5 border-t border-[#17233D]/10 pt-4">
                  <p className="mb-3 text-sm font-semibold text-[#17233D]">
                    {t.tvSize}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[55, 65, 75, 77, 83, 85].map(size => (
                      <button
                        key={size}
                        className={chip(config.tvSizeInches === size)}
                        onClick={() =>
                          setConfig(c => ({
                            ...c,
                            tvSizeInches: size,
                            accessorySku: null,
                          }))
                        }
                      >
                        {size}&quot;
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}
            </section>
            <section>
              <h2 className="mb-3 text-sm font-semibold">{t.grille}</h2>
              <div className="flex flex-wrap gap-2">
                {(["S", "K", "W"] as CanvasGrilleCode[]).map(g => (
                  <button
                    key={g}
                    className={chip(config.grilleCode === g)}
                    onClick={() => setConfig(c => ({ ...c, grilleCode: g }))}
                  >
                    {
                      (locale === "zh-TW"
                        ? TW_GRILLE_NAMES
                        : PUBLIC_GRILLE_NAMES)[g]
                    }
                  </button>
                ))}
              </div>
            </section>
            <section>
              <h2 className="mb-3 text-sm font-semibold">{t.finish}</h2>
              <div className="grid gap-2 sm:grid-cols-2">
                {variant?.finishChoices.map(f => (
                  <button
                    key={f}
                    className={`flex items-center gap-3 border p-2 text-left transition ${isTaiwan ? "rounded-sm" : "rounded-xl"} ${config.finish === f ? selectedOption : plainOption}`}
                    onClick={() => setConfig(c => ({ ...c, finish: f }))}
                  >
                    <img
                      className={`h-12 w-12 object-cover ${isTaiwan ? "rounded-sm" : "rounded-lg"}`}
                      src={
                        resolveCanvasFinishImagePath(f, config.productFormat) ??
                        CANVAS_NEUTRAL_PREVIEW_IMAGE
                      }
                    />
                    <span className="text-sm">{localFinish(f, locale)}</span>
                  </button>
                ))}
              </div>
            </section>
            {config.productFormat === "tv" ? (
              <section>
                <h2 className="mb-3 text-sm font-semibold">{t.mount}</h2>
                <div className="space-y-2">
                  <button
                    className={`w-full border p-4 text-left transition ${isTaiwan ? "rounded-sm" : "rounded-xl"} ${!config.accessorySku ? selectedOption : plainOption}`}
                    onClick={() =>
                      setConfig(c => ({ ...c, accessorySku: null }))
                    }
                  >
                    {t.includedMount}
                  </button>
                  <button
                    className={`w-full border p-4 text-left transition ${isTaiwan ? "rounded-sm" : "rounded-xl"} ${config.accessorySku === CANVAS_OWN_WALL_MOUNT_SKU ? selectedOption : plainOption}`}
                    onClick={() =>
                      setConfig(c => ({
                        ...c,
                        accessorySku: CANVAS_OWN_WALL_MOUNT_SKU,
                      }))
                    }
                  >
                    <span className="flex justify-between gap-3">
                      <strong className="text-sm">
                        {locale === "zh-TW"
                          ? t.ownMount
                          : CANVAS_OWN_WALL_MOUNT_NAME}
                      </strong>
                      <strong>{money.format(0)}</strong>
                    </span>
                  </button>
                  {accessories.map(a => {
                    const imagePath =
                      "imagePath" in a && typeof a.imagePath === "string"
                        ? a.imagePath
                        : null;
                    const selected = config.accessorySku === a.sku;
                    return (
                      <div
                        key={a.sku}
                        className={`flex overflow-hidden border transition ${isTaiwan ? "rounded-sm" : "rounded-xl"} ${selected ? selectedOption : plainOption}`}
                      >
                        <button
                          className="min-w-0 flex-1 p-4 text-left"
                          onClick={() =>
                            setConfig(c => ({ ...c, accessorySku: a.sku }))
                          }
                        >
                          <span className="flex justify-between gap-3">
                            <strong className="text-sm">
                              {localAccessory(a, locale)}
                            </strong>
                            <strong>{money.format(Number(a.sgd))}</strong>
                          </span>
                          {a.sku === "PS80" ? (
                            <span className="mt-2 block text-xs opacity-70">
                              {localAccessoryDescription(a, locale)}
                            </span>
                          ) : imagePath ? (
                            <span className="mt-2 block text-xs opacity-70">
                              {t.selectStand}
                            </span>
                          ) : null}
                        </button>
                        {imagePath && selected ? (
                          <button
                            type="button"
                            className="group relative m-2 w-24 shrink-0 overflow-hidden rounded-lg bg-white"
                            aria-label={`${t.enlargeImage} ${localAccessory(a, locale)}`}
                            onClick={() =>
                              setExpandedAccessory({
                                name: localAccessory(a, locale),
                                imagePath,
                              })
                            }
                          >
                            <img
                              src={imagePath}
                              alt={localAccessory(a, locale)}
                              className="h-full min-h-20 w-full object-cover transition group-hover:scale-105"
                            />
                            <span className="absolute inset-x-0 bottom-0 bg-black/65 px-1 py-1 text-center text-[10px] font-medium text-white">
                              {t.enlarge}
                            </span>
                          </button>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}
          </div>
          <aside
            className={`h-fit overflow-hidden border bg-white lg:sticky ${isTaiwan ? "rounded-sm border-black/10 border-t-2 border-t-[#D71920] shadow-[0_18px_55px_rgba(0,0,0,0.07)] lg:top-24" : "rounded-2xl border-[#17233D]/10 lg:top-5"}`}
          >
            <img
              className="h-64 w-full object-cover"
              src={
                resolveCanvasFinishImagePath(
                  config.finish,
                  config.productFormat
                ) ?? CANVAS_NEUTRAL_PREVIEW_IMAGE
              }
              alt={`${localFinish(config.finish, locale)} Canvas`}
            />
            <div className="space-y-4 p-5">
              <div>
                <p className="font-semibold">
                  {variant
                    ? localCanvasModel(variant.model, config, locale)
                    : t.selectConfiguration}
                </p>
                <p
                  className={`text-sm ${isTaiwan ? "text-black/50" : "text-[#17233D]/60"}`}
                >
                  {localFinish(config.finish, locale)}
                </p>
              </div>
              {accessory ? (
                <div className="flex justify-between border-t pt-3 text-sm">
                  <span>{localAccessory(accessory, locale)}</span>
                  <span>{money.format(Number(accessory.sgd))}</span>
                </div>
              ) : config.accessorySku === CANVAS_OWN_WALL_MOUNT_SKU ? (
                <div className="flex justify-between border-t pt-3 text-sm">
                  <span>
                    {locale === "zh-TW"
                      ? t.ownMount
                      : CANVAS_OWN_WALL_MOUNT_NAME}
                  </span>
                  <span>{money.format(0)}</span>
                </div>
              ) : null}
              <div className="flex justify-between border-t pt-4 text-xl">
                <span>{t.total}</span>
                <strong>{money.format(total)}</strong>
              </div>
              <div className="border-t pt-4">
                <h2 className="font-semibold">{t.sendProposal}</h2>
                <Input
                  className={`mt-3 ${isTaiwan ? "rounded-sm border-black/20 focus-visible:ring-[#D71920]" : ""}`}
                  placeholder={t.yourName}
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <button
                    className={chip(channel === "email")}
                    onClick={() => {
                      setChannel("email");
                      setChallengeId(null);
                      setVerificationToken(null);
                    }}
                  >
                    <Mail className="mr-1 inline h-4 w-4" />
                    {t.email}
                  </button>
                  <button
                    className={chip(channel === "phone")}
                    onClick={() => {
                      setChannel("phone");
                      setChallengeId(null);
                      setVerificationToken(null);
                    }}
                  >
                    <Smartphone className="mr-1 inline h-4 w-4" />
                    WhatsApp
                  </button>
                </div>
                <Input
                  className={`mt-3 ${isTaiwan ? "rounded-sm border-black/20 focus-visible:ring-[#D71920]" : ""}`}
                  type={channel === "email" ? "email" : "tel"}
                  placeholder={
                    channel === "email" ? t.emailAddress : t.mobileNumber
                  }
                  value={destination}
                  onChange={e => setDestination(e.target.value)}
                />
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  value={website}
                  onChange={e => setWebsite(e.target.value)}
                />
                {!challengeId ? (
                  <Button
                    className={`mt-3 w-full ${isTaiwan ? "rounded-sm bg-[#D71920] hover:bg-[#B51218]" : "bg-[#17233D]"}`}
                    disabled={!name || !destination || requestOtp.isPending}
                    onClick={sendCode}
                  >
                    {t.sendCode}
                  </Button>
                ) : !verificationToken ? (
                  <>
                    <Input
                      className={`mt-3 text-center tracking-[.4em] ${isTaiwan ? "rounded-sm border-black/20 focus-visible:ring-[#D71920]" : ""}`}
                      inputMode="numeric"
                      maxLength={6}
                      placeholder={t.sixDigitCode}
                      value={code}
                      onChange={e => setCode(e.target.value.replace(/\D/g, ""))}
                    />
                    <Button
                      className={`mt-3 w-full ${isTaiwan ? "rounded-sm bg-[#D71920] hover:bg-[#B51218]" : "bg-[#17233D]"}`}
                      disabled={code.length !== 6 || verifyOtp.isPending}
                      onClick={verify}
                    >
                      {t.verify}
                    </Button>
                  </>
                ) : (
                  <>
                    <label
                      className={`mt-4 flex items-start gap-2 text-xs ${isTaiwan ? "text-black/60" : "text-[#17233D]/70"}`}
                    >
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={e => setConsent(e.target.checked)}
                        className="mt-0.5"
                      />
                      {t.marketing}
                    </label>
                    <Button
                      className={`mt-4 w-full ${isTaiwan ? "rounded-sm bg-[#D71920] hover:bg-[#B51218]" : "bg-[#17233D]"}`}
                      disabled={submit.isPending}
                      onClick={finish}
                    >
                      {submit.isPending ? t.creating : t.sendMine}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
      {expandedAccessory ? (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 p-4"
          role="dialog"
          aria-modal="true"
          aria-label={expandedAccessory.name}
          onClick={() => setExpandedAccessory(null)}
        >
          <div
            className={`relative max-h-[92vh] max-w-4xl overflow-hidden bg-white shadow-2xl ${isTaiwan ? "rounded-sm" : "rounded-2xl"}`}
            onClick={event => event.stopPropagation()}
          >
            <button
              type="button"
              className={`absolute right-3 top-3 z-10 rounded-full p-2 text-white shadow-lg ${isTaiwan ? "bg-[#D71920]" : "bg-[#17233D]"}`}
              aria-label={t.closeImage}
              onClick={() => setExpandedAccessory(null)}
            >
              <X className="h-5 w-5" />
            </button>
            <img
              src={expandedAccessory.imagePath}
              alt={expandedAccessory.name}
              className="max-h-[82vh] w-auto max-w-[92vw] object-contain"
            />
            <p className="px-5 py-4 text-sm font-semibold text-[#17233D]">
              {expandedAccessory.name}
            </p>
          </div>
        </div>
      ) : null}
    </Shell>
  );
}
